import { useQuery } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import type { TracesListParams } from '../types'

/**
 * Query key factory for traces-related queries
 */
export const tracesKeys = {
  all: ['traces'] as const,
  lists: () => [...tracesKeys.all, 'list'] as const,
  list: (params?: TracesListParams) => [...tracesKeys.lists(), params] as const,
  details: () => [...tracesKeys.all, 'detail'] as const,
  detail: (id: string) => [...tracesKeys.details(), id] as const,
}

/**
 * Hook to fetch a paginated list of traces (runs)
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useTraces({ status: 'failed', limit: 20 })
 * console.log(data?.runs)
 * ```
 */
export function useTraces(params?: TracesListParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: tracesKeys.list(params),
    queryFn: () => client.traces.list(params),
  })
}

/**
 * Hook to fetch a single trace with all its steps
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useTrace('run-123')
 * console.log(data?.steps)
 * ```
 */
export function useTrace(runId: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: tracesKeys.detail(runId),
    queryFn: () => client.traces.get(runId),
    enabled: options?.enabled !== false && !!runId,
  })
}
